import {
  BaseMutationResponse2,
  Response,
  ResponseApolloErrorMapping,
} from '@contexts/Shared';
import { useMutation } from '@apollo/client';
import {
  AddDocumentCommand,
  DocumentsCommandActions,
  AddDocumentT,
  ReplaceDocumentT,
  ReplaceDocumentCommand,
  DeleteDocumentT,
  DeleteDocumentCommand,
  CreateDocumentationCommand,
  SpecifyDocumentationTypeCommand,
} from '../../../domain';
import { addDocument, replaceDocument } from '../schemas';
import {
  AddDocumentCommandResponseDto,
  AddDocumentCommandRequestDto,
  ReplaceDocumentCommandRequestDto,
  ReplaceDocumentCommandResponseDto,
  DeleteDocumentCommandResponseDto,
  DeleteDocumentCommandRequestDto,
  CreateDocumentationRequestDto,
  CreateDocumentationCommandResponseDto,
  SpecifyDocumentationTypeRequestDto,
  SpecifyDocumentationTypeCommandResponseDto,
} from '../dtos';
import { deleteDocument } from '../schemas/mutations/delete-document.mutation';
import {
  createDocumentationSchema,
  specifyDocumentationTypeSchema,
} from '../schemas/mutations/create-documentation.mutation';
import { useMemo } from 'react';

export const GqlDocumentsCommandActions: DocumentsCommandActions = {
  useAddDocument: (): AddDocumentT => {
    const [action, { loading, data, error }] = useMutation<
      AddDocumentCommandResponseDto,
      AddDocumentCommandRequestDto
    >(addDocument, {
      errorPolicy: 'all',
    });

    const errorResult = useMemo(
      () => error && ResponseApolloErrorMapping(error),
      [error]
    );

    return {
      loading,
      documentAdded: data?.addDocument.value,
      error: errorResult,
      addDocument: (command: AddDocumentCommand) =>
        action({
          variables: {
            file: command.file,
            creditStudyId: command.creditStudyId.value,
            documentType: command.documentType,
          },
        }),
    };
  },

  useDeleteDocument: (): DeleteDocumentT => {
    const [action, { loading, data, error }] = useMutation<
      DeleteDocumentCommandResponseDto,
      DeleteDocumentCommandRequestDto
    >(deleteDocument, {
      errorPolicy: 'all',
    });

    const errorResult = useMemo(
      () => error && ResponseApolloErrorMapping(error),
      [error]
    );

    return {
      loading,
      documentDeleted: data?.deleteDocument.value,
      error: errorResult,
      deleteDocument: (command: DeleteDocumentCommand) =>
        action({
          variables: {
            creditStudyId: command.creditStudyId.value,
            documentId: command.documentId.value,
          },
        }),
    };
  },

  useReplaceDocument: (): ReplaceDocumentT => {
    const [action, { loading, data, error }] = useMutation<
      ReplaceDocumentCommandResponseDto,
      ReplaceDocumentCommandRequestDto
    >(replaceDocument, {
      errorPolicy: 'all',
    });

    const errorResult = useMemo(
      () => error && ResponseApolloErrorMapping(error),
      [error]
    );

    return {
      loading,
      documentReplaced: data?.replaceDocument.value,
      error: errorResult,
      replaceDocument: (command: ReplaceDocumentCommand) =>
        action({
          variables: {
            file: command.file,
            creditStudyId: command.creditStudyId.value,
            documentId: command.documentId.value,
          },
        }),
    };
  },

  useCreateDocumentation: (): BaseMutationResponse2<
    CreateDocumentationCommand,
    string
  > => {
    const [action, { loading, data, error }] = useMutation<
      CreateDocumentationCommandResponseDto,
      CreateDocumentationRequestDto
    >(createDocumentationSchema, {
      errorPolicy: 'all',
    });

    const result: Response<string> = useMemo(
      () =>
        !!error
          ? new Response.Failure(ResponseApolloErrorMapping(error))
          : !data?.createApplicationDocumentation?.value
          ? Response.notPerformed
          : new Response.Success(data.createApplicationDocumentation.value),
      [error, data]
    );

    return {
      executing: loading,
      result,
      execute: (command: CreateDocumentationCommand) =>
        action({
          variables: {
            creditStudyId: command.creditStudy.value,
          },
        }),
    };
  },

  useSpecifyDocumentationType: (): BaseMutationResponse2<
    SpecifyDocumentationTypeCommand,
    string
  > => {
    const [action, { loading, data, error }] = useMutation<
      SpecifyDocumentationTypeCommandResponseDto,
      SpecifyDocumentationTypeRequestDto
    >(specifyDocumentationTypeSchema, {
      errorPolicy: 'all',
    });

    const result: Response<string> = useMemo(
      () =>
        !!error
          ? new Response.Failure(ResponseApolloErrorMapping(error))
          : !data?.specifyApplicationDocumentationType?.value
          ? Response.notPerformed
          : new Response.Success(
              data.specifyApplicationDocumentationType.value
            ),
      [error, data]
    );

    return {
      executing: loading,
      result,
      execute: (command: SpecifyDocumentationTypeCommand) =>
        action({
          variables: {
            documentation: command.documentation.value,
            declaresRent: command.userDeclaresRent,
            employmentSituation: command.employmentSituation,
          },
        }),
    };
  },
};
